import React, { FC, useCallback, useState, useEffect } from 'react';
import {
    HttpError,
    IResourceComponentsProps,
    useNavigation,
    useTranslate,
} from '@refinedev/core';
import dayjs from 'dayjs';
import { Form, FormProps, Spin } from 'antd';
import { useForm } from '@refinedev/antd';
import { FormInstance } from 'antd/es/form';
import type { UploadFile } from 'antd/es/upload/interface';

import { TranslatedBreadcrumbs } from 'components/translated-breadcrumbs';
import { useNotification } from 'hooks/useNotification';
import { handleFormError } from 'utils/error-handlers';
import { InternalLayout } from 'layouts/InternalLayout';

import { useWizardForm } from './hooks/useWizardForm';
import { StepNavigation } from 'components/preliminary-application/steps/StepNavigation';
import { Step1DrugInfo } from 'components/preliminary-application/steps/Step1DrugInfo';
import { Step2CertificateProcedure } from 'components/preliminary-application/steps/Step2CertificateProcedure';
import { Step3GeneralInfo } from 'components/preliminary-application/steps/Step3GeneralInfo';
import { Step4Documents } from 'components/preliminary-application/steps/Step4Documents';
import { Step5Submission } from 'components/preliminary-application/steps/Step5Submission';

import styles from './create.module.scss';
import { IFormValues } from '../../../interfaces/preliminary-application.interface';

export const PreliminaryApplicationCreate: FC<
    IResourceComponentsProps
> = () => {
    const t = useTranslate();
    const { replace } = useNavigation();
    const { msgSuccess, msgError } = useNotification();

    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // Store goToStep in a ref so it can be used in onMutationError callback
    const goToStepRef = React.useRef<((step: number) => void) | null>(null);

    const { formProps } = useForm<IFormValues, HttpError>({
        onMutationSuccess: () => {},
        onMutationError: (error: HttpError) => {
            // Use universal error handler with step navigation
            handleFormError({
                e: error,
                form,
                setIsFormProcessing: () => {}, // Handled by useWizardForm
                setDisSubmit: () => {}, // Handled by useWizardForm
                msgError,
                t,
                navigateToStep: goToStepRef.current || undefined,
                redirectToErrorScreen: () => {
                    // Optional: redirect to a specific error page or back to list
                    // replace('/preliminary-applications');
                },
            });
        },
        redirect: false,
        resource: 'preliminary-request',
    });

    const form = formProps?.form as FormInstance;

    const {
        currentStep,
        isFormProcessing,
        disSubmit,
        goToStep,
        goNext,
        goPrevious,
        saveDraft,
        submitFinal,
    } = useWizardForm({
        form,
        fileList,
        setFileList,
        msgSuccess,
        msgError,
        t,
        replace,
        requiredFieldsMap: {},
    });

    // Update the ref when goToStep is available
    useEffect(() => {
        if (goToStep) {
            goToStepRef.current = goToStep;
        }
    }, [goToStep]);

    const onValuesChange: FormProps['onValuesChange'] = (
        changedValues: Record<string, any>
    ) => {
        if (!form) return;

        const fieldsToReset =
            Object.keys(changedValues ?? {}).map(fieldName => ({
                name: fieldName
                    .split('.')
                    .map(part => (Number.isInteger(+part) ? +part : part)),
                errors: [],
            })) ?? [];

        if (fieldsToReset.length) {
            form.setFields(fieldsToReset);
        }
    };

    const handleFileChange = useCallback(
        (newFileList: UploadFile[]) => {
            setFileList(newFileList);
            form?.setFieldsValue({ files: newFileList });

            // Save file list metadata to localStorage for persistence
            const fileMetadata = newFileList.map(file => ({
                uid: file.uid,
                name: file.name,
                size: (file as any).size,
                type: (file as any).type,
                lastModifiedDate: (file as any).lastModifiedDate,
            }));
            localStorage.setItem('wizard-files', JSON.stringify(fileMetadata));
        },
        [form]
    );

    // Restore form data from localStorage on component mount
    useEffect(() => {
        const savedDraft = localStorage.getItem('wizard-draft');
        const savedFiles = localStorage.getItem('wizard-files');

        if (savedDraft && form) {
            try {
                const parsedData = JSON.parse(savedDraft);
                // Restore date as dayjs object
                if (parsedData.reg_date) {
                    parsedData.reg_date = dayjs(parsedData.reg_date);
                }
                form.setFieldsValue(parsedData);

                // Restore file list from separate localStorage item
                if (savedFiles) {
                    const fileMetadata = JSON.parse(savedFiles);
                    // Convert metadata back to UploadFile objects
                    const restoredFiles = fileMetadata.map((meta: any) => ({
                        uid: meta.uid,
                        name: meta.name,
                        size: meta.size,
                        type: meta.type,
                        lastModifiedDate: meta.lastModifiedDate,
                        status: 'done',
                    }));
                    setFileList(restoredFiles);
                    form.setFieldsValue({ files: restoredFiles });
                }
            } catch (error) {
                console.error(
                    'Failed to restore form data from localStorage:',
                    error
                );
            }
        }
    }, [form]);

    const formPropsForCreate: FormProps = {
        ...formProps,
        initialValues: {
            reg_date: dayjs(),
            is_electronic_form: false,
            is_mibp: false,
            is_ectd: false,
            medication_name: '',
            release_form: '',
            international_name_id: null,
            certificate: '',
            applicant_id: null,
            procedure_id: null,
            procedure_desc: '',
            notification_email: '',
            application_notes: '',
            files: [],
        },
        layout: 'vertical',
        onFinish: submitFinal,
        onValuesChange: onValuesChange,
        disabled: isFormProcessing,
        preserve: true,
    };

    const formId = 'preliminary-application-create-form';

    return (
        <InternalLayout
            breadcrumbs={<TranslatedBreadcrumbs />}
            onBack={() => replace('/preliminary-applications')}
            title={t(
                'preliminary-applications.titles.create',
                undefined,
                'Заявка ЄВ'
            )}
            contentClassName={styles.internalLayout}
            step={{
                current: currentStep + 1,
                total: 5,
                label: `Крок ${currentStep + 1} із 5`,
            }}
        >
            <Spin spinning={isFormProcessing}>
                <Form
                    {...formPropsForCreate}
                    id={formId}
                    className={styles.formContainer}
                >
                    {currentStep === 0 && <Step1DrugInfo />}
                    {currentStep === 1 && <Step2CertificateProcedure />}
                    {currentStep === 2 && (
                        <Step3GeneralInfo
                            showApplicationNotes
                            applicationNotesLabel={t(
                                'preliminary-applications-wp.fields.application_notes',
                                'Примітки до заяви'
                            )}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step4Documents
                            fileList={fileList}
                            onFileChange={handleFileChange}
                        />
                    )}
                    {currentStep === 4 && (
                        <Step5Submission fileList={fileList} />
                    )}

                    <StepNavigation
                        currentStep={currentStep}
                        totalSteps={5}
                        onPrevious={goPrevious}
                        onNext={goNext}
                        onSave={saveDraft}
                        isProcessing={isFormProcessing}
                        disSubmit={disSubmit}
                        showSubmitButton={false}
                    />
                </Form>
            </Spin>
        </InternalLayout>
    );
};
